import cn from 'classnames';
import { setCookie } from 'cookies-next';
import { NextPage } from 'next';
import { useSession } from 'next-auth/react';
import { useUserAgent } from 'next-useragent';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { NextSeo, NextSeoProps } from 'next-seo';
import { ToysManager } from '@data/toys/ToysManager';
import { ExpectedProps, ServerPropsFactory } from '@lib/factory/serverprops';
import { cookies } from '@specs/features/cookies';
import { LinksEnum, PageQueries } from '@specs/navigation';
import { IRedirectDataCookie } from '@specs/types';
import { ArtistView, Toy } from '@specs/views';
import { useGetToyDataOnPage } from '@hooks/useGetToyDataOnPage';
import { translateType, useTranslation } from '@hooks/useTranslation';
import { LazyBlock } from '@components/LazyBlock/LazyBlock';
import { PageLayout } from '@components/PageLayout/PageLayout';
import { AboutToy } from '@components/Pages/ToyPage/AboutToy';
import { ArtistSection } from '@components/Pages/ToyPage/ArtistSection/ArtistSection';
import { CollectionSection } from '@components/Pages/ToyPage/CollectionSection/CollectionSection';
import { GeneratedCodesSection } from '@components/Pages/ToyPage/GeneratedCodesSection/GeneratedCodesSection';
import { HistorySection } from '@components/Pages/ToyPage/HistorySection/HistorySection';
import { Info } from '@components/Pages/ToyPage/Info/Info';
import { MightLike } from '@components/Pages/ToyPage/MightLike/MightLike';
import { Modals } from '@components/Pages/ToyPage/Modals/Modals';
import { OffersSection } from '@components/Pages/ToyPage/OffersSection/OffersSection';
import { Preview } from '@components/Pages/ToyPage/Preview/Preview';
import { Breadcrumb } from '@components/UIKit/Breadcrumb/Breadcrumb';
import { Divider } from '@components/UIKit/Divider/Divider';
import { isNeedForceRedirect } from '../../../helpers/feature_drops_redirect';
import s from './toyPage.module.scss';
const getSeoConfig = (toy: Toy.Single, artist: ArtistView | null | undefined, tr: translateType): NextSeoProps => {
  const title = tr({
    id: 'toy.page.title'
  }, {
    name: toy.name,
    artistName: artist?.name
  });
  const description = tr({
    id: 'toy.page.description'
  }, {
    name: toy.name,
    artistName: artist?.name
  });
  const imageUrl = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/toys/main/${toy.imageid}.original@3x.webp`;
  return {
    title,
    description,
    openGraph: {
      title,
      description,
      images: [{
        url: imageUrl,
        width: 675,
        height: 675
      }]
    },
    additionalMetaTags: [{
      property: 'twitter:image',
      content: imageUrl
    }]
  };
};
export const getServerSideProps = ServerPropsFactory(async (props: ExpectedProps) => {
  const [initialToy] = await Promise.all([props?.slug ? ToysManager.getSingle(props.slug) : Promise.resolve(null)]);

  /** hotfix for some drops */
  if (props?.query?.secret && initialToy) {
    const redirect = await isNeedForceRedirect(initialToy.id, props.query.secret);
    if (redirect) {
      return {
        __redirect: redirect
      };
    }
  }
  return {
    ...props,
    initialToy: initialToy as Toy.Single
  } satisfies PageProps;
}, [{
  key: 'initialToy',
  destination: LinksEnum.ERROR_404
}]);
export interface PageProps extends ExpectedProps {
  initialToy: Toy.Single;
}
const Page: NextPage<PageProps> = ({
  initialToy,
  agent
}) => {
  const router = useRouter();
  const {
    translate: tr
  } = useTranslation();
  const {
    secret
  }: PageQueries = router.query;
  const {
    status
  } = useSession();
  const {
    isTablet,
    isMobile
  } = useUserAgent(agent ?? '');
  const canScanQR = isMobile || isTablet;
  const isPreview = useMemo(() => LinksEnum.TOY_PREVIEW === router.pathname && initialToy.preview, [initialToy]);
  useEffect(() => {
    if (isPreview || !secret || status !== 'unauthenticated') return;
    setCookie(cookies.REDIRECT_AFTER_AUTH_COOKIE_KEY, {
      secret,
      url: LinksEnum.TOY.replace('[slug]', initialToy.slug)
    } satisfies IRedirectDataCookie);
  }, [initialToy, secret, status]);
  useEffect(() => {
    if (isPreview || status === 'unauthenticated') return;
    cookies.remove(cookies.REDIRECT_AFTER_AUTH_COOKIE_KEY);
    return () => cookies.remove(cookies.REDIRECT_AFTER_AUTH_COOKIE_KEY);
  }, [status]);
  const {
    isArtClub,
    haveFigurinesForSale,
    isArtClubMember,
    isCollectable,
    isLoading,
    currentDrop,
    artists,
    artist,
    collection,
    firstStoreItem,
    myFirstStoreItem,
    isBonusToy,
    toy: currentToy,
    isHaveToyInCollection,
    iAmTheCreator
  } = useGetToyDataOnPage({
    toyId: initialToy.id
  });

  //  После получения фигурки из клиентского хука, используем данные из него.
  //  Это помогает корректно инвалидировать кэш и избежать проблем с гидрацией
  const toy = isPreview ? initialToy : currentToy ?? initialToy;
  const seoConfig = useMemo(() => getSeoConfig(toy, artist, tr), [toy, artist, tr]);
  return <PageLayout showBackIconOnMobile data-sentry-element="PageLayout" data-sentry-component="Page" data-sentry-source-file="index.tsx">
            <NextSeo {...seoConfig} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />
            <div className={s.toyPage}>
                <div className={cn(s.toyPage__container, '_container')}>
                    <Breadcrumb slugTitle={toy.name} data-sentry-element="Breadcrumb" data-sentry-source-file="index.tsx" />
                    <div className={s.toyPage__body}>
                        <Preview {...toy} canScanQR={canScanQR} showModelViewer={isPreview ? true : isHaveToyInCollection || iAmTheCreator} data-sentry-element="Preview" data-sentry-source-file="index.tsx" />
                        <div className={s.toyPage__right}>
                            <Info {...toy} artist={artist} collection={collection} isArtClubMember={isArtClubMember} isCollectable={isCollectable} currentDrop={currentDrop} haveFigurinesForSale={haveFigurinesForSale} isArtClub={isArtClub} firstStoreItem={firstStoreItem} isLoading={isLoading} myFirstStoreItem={myFirstStoreItem} isBonusToy={isBonusToy} isHaveToyInCollection={isHaveToyInCollection} iAmTheCreator={iAmTheCreator} data-sentry-element="Info" data-sentry-source-file="index.tsx" />
                            <div className={s.toyPage__accordions}>
                                {toy?.description && <AboutToy description={toy.description} />}
                                <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
                                <CollectionSection collection={collection} data-sentry-element="CollectionSection" data-sentry-source-file="index.tsx" />
                                <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
                                <ArtistSection artists={artists} data-sentry-element="ArtistSection" data-sentry-source-file="index.tsx" />
                                <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
                            </div>
                            {!isPreview && <div className={s.toyPage__lists}>
                                    <LazyBlock>
                                        {({
                  inView,
                  ref
                }) => <OffersSection blockRef={ref} toyId={toy.id} slug={toy.slug} toyName={toy.name} inView={inView} />}
                                    </LazyBlock>
                                    <LazyBlock>
                                        {({
                  inView,
                  ref
                }) => <HistorySection blockRef={ref} toyId={toy.id} artistId={artist?.id} inView={inView} />}
                                    </LazyBlock>
                                    {iAmTheCreator && toy.publication === 'TWIN' && <LazyBlock>
                                            {({
                  inView,
                  ref
                }) => <GeneratedCodesSection inView={inView} blockRef={ref} toyId={toy.id} name={toy.name} slug={toy.slug} />}
                                        </LazyBlock>}
                                </div>}
                        </div>
                    </div>
                    {collection && <MightLike collectionId={collection.id} />}
                </div>
            </div>
            <Modals toyName={toy.name} data-sentry-element="Modals" data-sentry-source-file="index.tsx" />
        </PageLayout>;
};
export default Page;